
import React, { useEffect, useState } from 'react';
import {
  TouchGestureContext,
  type TouchGesture,
} from './touch-gesture-context';

const initialGesture: TouchGesture = {
  pinchActive: false,
  pinchScale: 1,
  lastPinchDistance: 0,
};

export function TouchGestureProvider({ children }: { children: React.ReactNode }) {
  const [enableGestures, setEnableGestures] = useState(true);
  const [gesture] = useState<TouchGesture>(initialGesture);
  const [touchEnabled, setTouchEnabled] = useState(false);
  const [isFormDialogOpen, setFormDialogOpen] = useState(false);

  // Detect if touch is enabled on the device
  useEffect(() => {
    setTouchEnabled('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Prevent iOS zoom on double-tap
  useEffect(() => {
    if (!touchEnabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      // If we're in a form input or dialog, we want to prevent multi-touch gestures
      if (isFormDialogOpen && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // This handler prevents the zoom behavior when tapping on inputs
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      
      if (isFormDialogOpen && 
          (target.tagName === 'INPUT' || 
           target.tagName === 'TEXTAREA' || 
           target.tagName === 'SELECT')) {
        // Add prevention of viewport scale/zoom
        document.documentElement.style.setProperty('touch-action', 'pan-x pan-y');
      }
    };
    
    const handleBlur = () => {
      // Reset touch action when input is blurred
      if (isFormDialogOpen) {
        document.documentElement.style.setProperty('touch-action', '');
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        const target = e.target as HTMLElement;
        if (target.closest('.react-flow') || target.closest('.pinch-zoom-enabled')) {
          e.preventDefault();
        }
      }
    };

    // Add input-specific font size to prevent auto-zoom
    const addInputStyles = () => {
      const style = document.createElement('style');
      style.id = 'input-zoom-prevention';
      style.innerHTML = `
        @media (max-width: 768px) {
          input, textarea, select {
            font-size: 16px !important; /* Prevents iOS zoom */
          }
          .dialog-content {
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
          }
        }
      `;
      document.head.appendChild(style);
    };

    addInputStyles();
    
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
      
      const style = document.getElementById('input-zoom-prevention');
      if (style) style.remove();
    };
  }, [touchEnabled, enableGestures, isFormDialogOpen]);

  return (
    <TouchGestureContext.Provider value={{ 
      enableGestures, 
      setEnableGestures, 
      gesture, 
      touchEnabled,
      isFormDialogOpen,
      setFormDialogOpen
    }}>
      {children}
    </TouchGestureContext.Provider>
  );
}


